/* This should be the first import after polyfills to ensure datadog
   is initialized before everything else */
import './utils/datadog';

/* Root Styles: The first thing since they should have the less precendence */
import '@dtvgo/font-faces';
import './index.scss';

/* External */
import { Provider, ReactReduxContext } from 'react-redux';
import { render } from 'react-dom';
import { Router } from 'react-router';

/* Components */
import { App } from './app';
import ConfigProvider from './providers/ConfigProvider';
import ConnectionStatusProvider from './providers/ConnectionStatusProvider';
import CurrentTimeProvider from './providers/CurrentTimeProvider';
import CursorStateProvider from './providers/CursorStateProvider';
import DeviceInfoProvider from './providers/DeviceInfoProvider';
import InterstitialProvider from './providers/InterstitialProvider';
import IntlProviderWrapper from './providers/IntlProviderWrapper';
import KeyBoardStateProvider from './providers/KeyBoardStateProvider';
import LoadingProvider from './providers/LoadingProvider';
import RestoreFocusProvider from './providers/RestoreFocusProvider';
import { SidebarStateProvider } from './providers/SidebarStateContext';
import UserProvider from './providers/UserProvider';
import ForegroundAppProvider from './providers/ForegroundAppProvider';
import TermsOfServiceProvider from './providers/TermsOfServiceProvider';
import QueryParamsProvider from './providers/QueryParamsProvider';

/* Storage */
import store from './storage/configureStore';

/* Utils */
import { ErrorType } from './utils/constants';
import { APP_VERSION } from './utils/env';
import { initSpatialNavigation } from './utils/spatialNavigation';
import { history } from './utils/history';
import platform from './utils/platform';
import { reportLoadDrmAgentError } from './services/gaService';

const loaderContainer = document.querySelector('.loader-container');
const showLoading = () => loaderContainer.classList.remove('loader--hide');
const hideLoading = () => loaderContainer.classList.add('loader--hide');

initSpatialNavigation();
platform.init((error) => {
  if (error.type === ErrorType.LOAD_DRM_AGENT) {
    reportLoadDrmAgentError(error);
  }
});

render(
  <vega-app allowSystemKeyEvents="true">
    <ConfigProvider>
      <IntlProviderWrapper>
        <Provider store={store} context={ReactReduxContext}>
          <Router history={history}>
            <LoadingProvider>
              <ForegroundAppProvider>
                <CurrentTimeProvider>
                  <DeviceInfoProvider>
                    <ConnectionStatusProvider>
                      <KeyBoardStateProvider>
                        <CursorStateProvider>
                          <RestoreFocusProvider>
                            <UserProvider>
                              <TermsOfServiceProvider>
                                <InterstitialProvider>
                                  <SidebarStateProvider>
                                    <QueryParamsProvider>
                                      <App
                                        hideLoading={hideLoading}
                                        showLoading={showLoading}
                                      />
                                    </QueryParamsProvider>
                                  </SidebarStateProvider>
                                </InterstitialProvider>
                              </TermsOfServiceProvider>
                            </UserProvider>
                          </RestoreFocusProvider>
                        </CursorStateProvider>
                      </KeyBoardStateProvider>
                    </ConnectionStatusProvider>
                  </DeviceInfoProvider>
                </CurrentTimeProvider>
              </ForegroundAppProvider>
            </LoadingProvider>
          </Router>
        </Provider>
      </IntlProviderWrapper>
    </ConfigProvider>
  </vega-app>,
  document.getElementById('root'),
);

async function logInfo() {
  const appIdPromise = platform.getAppId();
  const deviceInfoPromise = platform.getDeviceInfo();
  const versionAppPromise = platform.getAppVersion();

  const appId = await appIdPromise;
  const deviceInfo = await deviceInfoPromise;
  const versionId = await versionAppPromise;

  /* eslint-disable no-console -- Allow to log this info to the console */
  console.group('App & Device info');
  console.log('AppId:', appId);
  console.log('AppVersion:', APP_VERSION);
  console.log('DeviceInfo:', deviceInfo);
  console.log('VersionId:', versionId);
  console.groupEnd();
  /* eslint-enable no-console -- re-enable */
}

(function scheduleLogInfo() {
  setTimeout(logInfo, 0);
}());
